import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DeleteModal from "../../components/Common/DeleteModal";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { ToastContainer } from "react-toastify";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { map, isEmpty, size } from "lodash";

import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import SafeField from "helpers/SafeField";

import {
  addProjectStatus,
  getProjectStatus,
  updateProjectStatus,
  deleteProjectStatus,
} from "../../store/actions";

const ProjectStatus = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const { docstatuses, loading } = useSelector(
    (state) => state.ObjectReducer
  );

  const [docstatus, setDocstatus] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // =============================
  // Load Data
  // =============================
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    dispatch(getProjectStatus(userId));
  }, [dispatch]);

  // =============================
  // Toggle Offcanvas
  // =============================
  const toggleCanvas = useCallback(() => {
    setIsRight((prev) => !prev);
  }, []);

  const openEditCanvas = (data) => {
    setDocstatus({
      id: data.id,
      projectStatusId: data.projectStatusId,
      projectStatusTitle: data.projectStatusTitle,
      agentId: data.agentId,
    });
    setIsEdit(true);
    setIsRight(true);
  };

  const closeCanvas = () => {
    setDocstatus({});
    setIsEdit(false);
    setIsRight(false);
  };

  // =============================
  // Add Submit
  // =============================
  const handleProStatusSubmit = (values) => {
    dispatch(addProjectStatus(values, history));
  };

  // =============================
  // Delete
  // =============================
  const onClickDelete = (item) => {
    setDocstatus(item);
    setDeleteModal(true);
  };

  const handleDeleteProStatus = () => {
    if (docstatus?.id) {
      dispatch(deleteProjectStatus(docstatus));
      setDeleteModal(false);
    }
  };

  // =============================
  // Update Submit
  // =============================
  const handleUpdate = (values) => {
    const updateData = {
      id: values.id,
      projectStatusId: values.projectStatusId,
      projectStatusTitle: values.projectStatusTitle,
      agentId: values.agentId,
    };

    dispatch(updateProjectStatus(updateData));
    closeCanvas();
  };

  return (
    <React.Fragment>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProStatus}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
       

        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={t("General Settings")}
                breadcrumbItem={t("Project Status")}
              />
            </Col>
          </Row>

          <Row>
          
            <Col xl="12">
              {/* ================= Add Form ================= */}
              <Formik
                enableReinitialize
                initialValues={{
                  projectStatusTitle: "",
                }}
                validationSchema={Yup.object({
                  projectStatusTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleProStatusSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Card>
                      <CardBody>
                       

                        <Row>
                          <Col sm="3">
                            <FormGroup className="mb-3">
                              <Label>{t("Title *")}</Label>

                              <SafeField
                                name="projectStatusTitle"
                                type="text"
                                onChange={handleChange}
                                className={
                                  "form-control" +
                                  (errors.projectStatusTitle &&
                                  touched.projectStatusTitle
                                    ? " is-invalid"
                                    : "")
                                }
                              />

                              <ErrorMessage
                                name="projectStatusTitle"
                                component="div"
                                className="invalid-feedback"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="2">
                            <Button
                              type="submit"
                              color="primary"
                              style={{ marginTop: 31 }}
                            >
                              Add
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ================= History ================= */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title">History</h5>

                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {map(docstatuses, (item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.projectStatusTitle}</td>
                                  <td>
                                   
                                      <>
                                        <ActionEdit
                                          onClick={() =>
                                            openEditCanvas(item)
                                          }
                                        />
                                        <ActionDelete
                                          onClick={() =>
                                            onClickDelete(item)
                                          }
                                        />
                                      </>
                                   
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>

          {/* ================= Edit Offcanvas ================= */}
          <Offcanvas isOpen={isRight} direction="end" toggle={closeCanvas}>
            <OffcanvasHeader toggle={closeCanvas}>
              Update
            </OffcanvasHeader>

            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  id: docstatus?.id,
                  projectStatusId: docstatus?.projectStatusId,
                  projectStatusTitle: docstatus?.projectStatusTitle || "",
                  agentId: docstatus?.agentId,
                }}
                validationSchema={Yup.object({
                  projectStatusTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleUpdate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <FormGroup className="mb-3">
                      <Label>{t("Title *")}</Label>

                      <SafeField
                        name="projectStatusTitle"
                        type="text"
                        onChange={handleChange}
                        className={
                          "form-control" +
                          (errors.projectStatusTitle &&
                          touched.projectStatusTitle
                            ? " is-invalid"
                            : "")
                        }
                      />

                      <ErrorMessage
                        name="projectStatusTitle"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FormGroup>

                    <Button type="submit" color="primary">
                      Update
                    </Button>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProjectStatus;
